import { Button } from '@repo/ui/src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import { Label } from '@repo/ui/src/components/ui/label';
import { Slider } from '@repo/ui/src/components/ui/slider';
import { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import { cropToPngBlob, type CropPixels } from '@repo/ui/src/lib/cropImage';
import { validateLogoFile, LOGO_MIME_TYPES } from '../../../lib/logoFile';
import { presignUpload, uploadToR2 } from '../../../lib/upload';

interface LogoCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Receives the public R2 URL of the uploaded logo. */
  onUploaded: (fileUrl: string) => void;
}

export function LogoCropDialog({ open, onOpenChange, onUploaded }: LogoCropDialogProps) {
  const { t } = useTranslation('settings');
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState<CropPixels | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const onCropComplete = useCallback((_area: unknown, areaPixels: CropPixels) => {
    setCropPixels(areaPixels);
  }, []);

  const reset = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropPixels(null);
    setError(null);
    setUploading(false);
  };

  // Radix does not fire the Dialog's onOpenChange when the controlled `open`
  // prop changes, so the Cancel button (which calls the parent's setter
  // directly) must reset local state itself — otherwise reopening the
  // dialog shows the previous image and any stale error.
  const close = () => {
    reset();
    onOpenChange(false);
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);

    const fileError = validateLogoFile(file);
    if (fileError) {
      setError(fileError === 'TYPE' ? t('profile.crop.errType') : t('profile.crop.errSize'));
      return;
    }

    // Clear the previous image's crop rect/zoom/pixels — otherwise a confirm
    // that races react-easy-crop's onCropComplete could crop the new image
    // with the old image's rectangle.
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropPixels(null);

    const reader = new FileReader();
    reader.addEventListener('load', () => setImageSrc(String(reader.result)));
    reader.readAsDataURL(file);
  };

  /** Crop → PNG → presign → PUT to R2 → hand the public URL back. */
  const handleConfirm = async () => {
    if (!imageSrc || !cropPixels) return;
    setUploading(true);
    setError(null);

    try {
      const blob = await cropToPngBlob(imageSrc, cropPixels);

      const presign = await presignUpload({
        fileName: 'org-logo.png',
        fileType: 'image/png',
        fileSize: blob.size,
        docType: 'ORG_LOGO',
      });

      // uploadToR2 sends a plain PUT with no auth header, using blob.type as
      // Content-Type — cropToPngBlob always produces 'image/png', matching
      // the type the presigned URL was signed for.
      await uploadToR2(presign.uploadUrl, blob);

      // ORG_LOGO is a public document type, so the server always returns
      // the CDN URL; a null here is a server misconfiguration, not a path.
      if (!presign.fileUrl) throw new Error('missing public URL');
      onUploaded(presign.fileUrl);
      reset();
      onOpenChange(false);
    } catch {
      setError(t('profile.crop.errUpload'));
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Ignore close requests (X button, Escape, overlay click) while an
        // upload is in flight — otherwise the dialog vanishes but the upload
        // keeps running and still calls onUploaded on success, silently
        // changing the org's logo after the user believed they had cancelled.
        if (!next && uploading) return;
        if (!next) {
          close();
          return;
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('profile.crop.title')}</DialogTitle>
          <DialogDescription>{t('profile.crop.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <input
            type="file"
            accept={LOGO_MIME_TYPES.join(',')}
            onChange={handleFile}
            className="block w-full text-sm text-surface-600"
            aria-label={t('profile.crop.choose')}
          />

          {imageSrc && (
            <>
              <div className="relative w-full h-64 bg-surface-100">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
              <div className="space-y-1.5">
                <Label id="logo-zoom-label">{t('profile.crop.zoom')}</Label>
                <Slider
                  aria-labelledby="logo-zoom-label"
                  min={1}
                  max={3}
                  step={0.01}
                  value={[zoom]}
                  onValueChange={([next]) => setZoom(next ?? 1)}
                />
              </div>
            </>
          )}

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={uploading}>
            {t('profile.crop.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!imageSrc || !cropPixels || uploading}>
            {uploading ? t('profile.crop.uploading') : t('profile.crop.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
